import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { useDraggable, useDroppable } from '@dnd-kit/core';

import { Icon } from 'components';

import { OutcomeCardProps, SortableOutcomeItemProps } from './types';
import {
  getTransformStyle,
  outcomeCardClassName,
  outcomeCardDefaultClassName,
  outcomeCardDraggingOverlayClassName,
} from './config';

export const OutcomeCard = ({
  index,
  label,
  isDragging,
  description,
  isDraggingOverlay,
}: OutcomeCardProps) => {
  const { t } = useTranslation(['common']);
  const hasDescription = Boolean(description);

  return (
    <Box
      sx={{
        opacity: isDragging ? 0 : 1,
      }}
      className={twMerge(
        outcomeCardClassName,
        isDraggingOverlay
          ? outcomeCardDraggingOverlayClassName
          : outcomeCardDefaultClassName
      )}
    >
      <div className="order-1 min-w-0 flex-1 md:order-2">
        <div
          className={`flex min-w-0 items-start justify-between gap-2 ${hasDescription ? 'mb-2 border-b border-[#F2F5F7] pb-2' : ''}`}
        >
          <Typography className="text-black-primary min-w-0 flex-1 text-[14px] leading-5 font-semibold">
            {label}
          </Typography>
          <div className="bg-green-primary/10 shrink-0 rounded-[24px] px-2 py-[2px]">
            <Typography className="text-green-primary text-[10px] leading-[14px] font-normal whitespace-nowrap">
              {t('outcomePriority', { index: index + 1 })}
            </Typography>
          </div>
        </div>
        {description && (
          <Typography className="text-[14px] leading-5 font-normal text-[#9DA5B0]">
            {description}
          </Typography>
        )}
      </div>
      <Icon.Drag className="order-2 shrink-0 md:order-1" />
    </Box>
  );
};

export const SortableOutcomeItem = ({
  index,
  outcome,
}: SortableOutcomeItemProps) => {
  const { setNodeRef: setDroppableRef } = useDroppable({
    id: outcome.id,
  });
  const {
    listeners,
    transform,
    attributes,
    isDragging,
    setNodeRef: setDraggableRef,
  } = useDraggable({
    id: outcome.id,
  });

  const setNodeRef = (node: HTMLDivElement | null) => {
    setDroppableRef(node);
    setDraggableRef(node);
  };

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="touch-none"
      sx={{
        zIndex: isDragging ? 10 : undefined,
        transform: getTransformStyle(transform),
      }}
    >
      <OutcomeCard
        index={index}
        label={outcome.label}
        isDragging={isDragging}
        description={outcome.description}
      />
    </Box>
  );
};
